import { useState } from 'react';
import { acceptInvite, declineInvite, getMyInvites } from '../api/endpoints.js';
import { useAction, useApi, useIntentKey } from '../api/hooks.js';
import { clearInviteToken, pendingInviteToken } from '../flow/invite-link.js';
import {
  Async,
  Bool,
  Button,
  Failure,
  KeyValue,
  Note,
  Panel,
  Raw,
  Row,
  Screen,
} from '../ui/kit.js';

/**
 * Invites addressed to the signed-in person. The accepter is the SESSION, never
 * a body field — the legacy endpoint took `inviteePhone` from the request and
 * let anyone with an invite id accept as anyone.
 *
 * THERE IS NO TOKEN FIELD ANY MORE. This list is matched to the session by the
 * same predicate the accept route now accepts as proof, so an invite visible
 * here is one this session may accept outright; a 43-character paste out of the
 * person's own SMS proved nothing the session did not already prove. The
 * deep-link stash is still passed when present (flow/invite-link) and still
 * cleared on success, but nothing is typed and nothing is gated on it.
 *
 * Each invite renders its `consequences` BEFORE the call: the cancel is
 * immediate and refundless now, so the paid-through date being forfeited, the
 * plan being joined, and the OWNER_HAS_MEMBERS block all show up front.
 */
export function InvitesScreen() {
  const invites = useApi(getMyInvites, []);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const intent = useIntentKey();

  const accept = useAction(acceptInvite, () => {
    // The capability is spent with the invite — a stale stash must not leak
    // into the next visit's field.
    clearInviteToken();
    intent.next();
    invites.reload();
  });
  const decline = useAction(declineInvite, () => invites.reload());

  return (
    <Screen
      title="My invites"
      lede="Accepting cancels your own subscription immediately with no refund, so the backend refuses until you confirm that consequence explicitly — and refuses outright while your own household still has members."
    >
      <Async state={invites} empty="no invites addressed to you">
        {(data) => (
          <>
            {data.invites.length === 0 && <p className="empty">no invites addressed to you</p>}
            {data.invites.map((invite) => {
              const c = invite.consequences;
              const forfeitDate =
                c.current_period_end === null
                  ? null
                  : new Date(c.current_period_end).toLocaleDateString();
              return (
                <Panel key={invite.invite_id} title={`invite ${invite.invite_id}`}>
                  <KeyValue
                    rows={[
                      [
                        'invited_by',
                        `${invite.invited_by.first_name ?? ''} ${invite.invited_by.last_name ?? ''}`.trim() ||
                          'unnamed',
                      ],
                      ['message', invite.message],
                      ['created_at', invite.created_at],
                      ['expires_at', invite.expires_at],
                      ['joining_plan_code', c.joining_plan_code],
                      ['cancels_subscription', <Bool key="c" value={c.cancels_subscription} />],
                      ['plan_code', c.plan_code],
                      ['provider', c.provider],
                      ['effective', c.effective],
                      ['current_period_end', c.current_period_end],
                      ['refund', c.refund],
                      ['can_accept', <Bool key="a" value={c.can_accept} />],
                      ['blocked_reason', c.blocked_reason],
                    ]}
                  />
                  {!c.can_accept && (
                    <Note>
                      <strong>You cannot accept this invitation yet.</strong> Your own household
                      still has members — accepting would cancel the plan that covers them, so the
                      backend answers <code>OWNER_HAS_MEMBERS</code>. Remove your members or cancel
                      your plan first.
                    </Note>
                  )}
                  {c.can_accept && c.cancels_subscription && (
                    <Note>
                      Accepting cancels your <strong>{c.plan_code ?? 'current'}</strong> plan{' '}
                      <strong>immediately</strong> with <strong>no refund</strong>
                      {forfeitDate !== null && (
                        <>
                          {' '}
                          — you give up the time you already paid for, through{' '}
                          <strong>{forfeitDate}</strong>
                        </>
                      )}
                      {c.joining_plan_code !== null && (
                        <>
                          . You would be covered by the inviter&rsquo;s{' '}
                          <strong>{c.joining_plan_code}</strong> plan instead
                        </>
                      )}
                      . The backend answers <code>WOULD_CANCEL_SUBSCRIPTION</code> until{' '}
                      <code>confirm_cancel</code> is set.
                      {c.user_must_cancel_in_store && (
                        <>
                          {' '}
                          <strong>Your plan is billed by Apple:</strong> the App Store keeps
                          charging you until you cancel the subscription there yourself — this
                          server cannot do it for you.
                        </>
                      )}
                    </Note>
                  )}
                  <Row>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={confirmCancel}
                        onChange={(event) => setConfirmCancel(event.target.checked)}
                      />
                      confirm_cancel
                    </label>
                    <Button
                      onClick={() =>
                        accept.run(invite.invite_id, pendingInviteToken(), confirmCancel, intent.key)
                      }
                      disabled={accept.pending || !c.can_accept}
                    >
                      {accept.pending ? 'accepting…' : 'Accept'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => decline.run(invite.invite_id)}
                      disabled={decline.pending}
                    >
                      Decline
                    </Button>
                  </Row>
                </Panel>
              );
            })}
            <Raw label="GET /v1/me/invites" value={data} />
          </>
        )}
      </Async>

      {accept.error !== null && (
        <Failure
          error={accept.error}
          hint={
            {
              WOULD_CANCEL_SUBSCRIPTION: 'Tick confirm_cancel and try again with the same key.',
              OWNER_HAS_MEMBERS:
                'Your own household still has members — remove them or cancel your plan first.',
              INVITE_EXPIRED: 'This invite is past its expiry; ask for a resend.',
              ALREADY_IN_HOUSEHOLD: 'You already hold a seat somewhere.',
              HOUSEHOLD_FULL: 'The inviting household has no free seat left.',
              NOT_FOUND:
                'Unknown invite, or one not addressed to this session — the same answer either way, deliberately.',
            }[accept.error.code]
          }
        />
      )}
      {accept.result !== null && (
        <>
          <Note>
            You joined the household. If you were mid-setup, continue where you left off —{' '}
            <a href="#/">the app routes you to the next step</a>.
          </Note>
          <Raw label="accept response" value={accept.result} />
        </>
      )}
      {decline.error !== null && <Failure error={decline.error} />}
    </Screen>
  );
}
